import { useEffect, useRef, useState } from "react";
import { Zap, ClipboardCheck, Loader2, ChevronDown, CheckCircle2 } from "lucide-react";
import { Pill } from "@/components/perf";

const GLYPHS = "アイウエオカキクケコサシスセソ0123456789$+-*/=%#PICKLEBULLPEN";
const input = "w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red";
const lbl = "block text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5";

const TP_CSS = `
.tp-placard{animation:tpPanDown 1s cubic-bezier(.22,1,.36,1) both}
@keyframes tpPanDown{0%{transform:translateY(-108%);opacity:0}55%{opacity:1}100%{transform:translateY(0);opacity:1}}
.tp-closing{animation:tpPanUp .8s ease-in both}
@keyframes tpPanUp{0%{transform:translateY(0);opacity:1}100%{transform:translateY(-112%);opacity:0}}
.tp-etch{opacity:0;animation:tpEtch .55s ease-out forwards}
@keyframes tpEtch{0%{opacity:0;filter:blur(5px) brightness(3)}60%{filter:blur(0) brightness(1.7)}100%{opacity:1;filter:blur(0) brightness(1)}}
@keyframes tpBlink{0%,100%{opacity:1}50%{opacity:.35}}
.tp-live-dot{animation:tpBlink 1.4s ease-in-out infinite}
`;

function RainCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf, cols, drops;
    const setup = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      cols = Math.ceil(canvas.width / 16);
      drops = Array.from({ length: cols }, () => Math.random() * -(canvas.height / 16));
    };
    setup();
    const draw = () => {
      ctx.fillStyle = "rgba(4,8,10,.14)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = "12px monospace";
      for (let i = 0; i < cols; i++) {
        ctx.fillStyle = "rgba(74,222,128,.35)";
        ctx.fillText(GLYPHS[Math.floor(Math.random() * GLYPHS.length)], i * 16, drops[i] * 16);
        drops[i] = drops[i] * 16 > canvas.height && Math.random() > 0.975 ? 0 : drops[i] + 0.5;
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", setup);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", setup); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-40 pointer-events-none" />;
}

const E = ({ d, children, className = "" }) => (
  <div className={`tp-etch ${className}`} style={{ animationDelay: `${d}s` }}>{children}</div>
);

function armOnGesture(ctx, start) {
  const h = () => { ctx.resume().then(start).catch(() => {}); };
  window.addEventListener("pointerdown", h, { once: true });
  window.addEventListener("keydown", h, { once: true });
}

function playMatchSiren() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const start = () => {
      const now = ctx.currentTime;
      const cycles = 3, dur = 0.85;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.6, now + 0.08);
      master.connect(ctx.destination);
      [0, 8].forEach((det) => {
        const o = ctx.createOscillator();
        o.type = "sawtooth";
        o.detune.value = det;
        for (let i = 0; i < cycles; i++) {
          const t = now + i * dur;
          o.frequency.setValueAtTime(420, t);
          o.frequency.linearRampToValueAtTime(960, t + dur * 0.5);
          o.frequency.linearRampToValueAtTime(420, t + dur);
        }
        o.connect(master);
        o.start(now);
        o.stop(now + cycles * dur + 0.15);
      });
      master.gain.setValueAtTime(0.6, now + cycles * dur - 0.2);
      master.gain.exponentialRampToValueAtTime(0.0001, now + cycles * dur + 0.12);
      setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, (cycles * dur + 0.6) * 1000);
    };
    if (ctx.state === "suspended") {
      ctx.resume().then(() => (ctx.state === "running" ? start() : armOnGesture(ctx, start))).catch(() => armOnGesture(ctx, start));
    } else start();
  } catch { /* audio unavailable */ }
}

export default function TournamentPlacard({ active, game, announcements, scoreFor, setScoreFor, scoreAgainst, setScoreAgainst, submitScore, busy }) {
  const pending = !!game?.pending_submission;
  const [phase, setPhase] = useState(pending ? "closed" : "open");
  const [openKey, setOpenKey] = useState(0);
  const prevPending = useRef(pending);

  useEffect(() => {
    if (phase === "open") playMatchSiren();
  }, [phase, openKey]);

  useEffect(() => {
    if (pending && !prevPending.current) {
      setPhase("confirm");
      const t1 = setTimeout(() => setPhase("closing"), 2600);
      const t2 = setTimeout(() => setPhase("closed"), 3400);
      prevPending.current = pending;
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    if (!pending && prevPending.current && game) {
      setOpenKey((k) => k + 1);
      setPhase("open");
    }
    prevPending.current = pending;
  }, [pending, game]);

  if (phase === "closed") {
    return (
      <div className="lg:col-span-2 border border-green-500/30 bg-black/70 p-4 flex items-center justify-between gap-3 cursor-pointer hover:border-green-400/60 transition-colors"
        data-testid="tournament-placard-closed" onClick={() => { setOpenKey((k) => k + 1); setPhase("open"); }}>
        <div className="flex items-center gap-3 min-w-0">
          <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-green-400" data-testid="score-pending-banner">
              Transmission received{game?.submitted_score ? ` — score ${game.submitted_score} submitted` : " — score submitted"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5" data-testid="consensus-progress">
              {game?.consensus?.reports
                ? <>
                    <span className="text-pb-cyan font-bold">{game.consensus.reports} of {game.consensus.needed} confirmations in</span>
                    {game.consensus.waiting_on?.length > 0 && ` · waiting on ${game.consensus.waiting_on.join(", ")}`}
                    {" — auto-verifies the moment enough scores match"}
                  </>
                : "Waiting on TD approval · the placard drops again when your next match starts"}
            </div>
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-green-500/70 shrink-0" />
      </div>
    );
  }

  const closing = phase === "closing";
  return (
    <div className="lg:col-span-2 relative overflow-hidden border border-green-500/25" style={{ background: "#03070a", minHeight: 320 }}
      data-testid="tournament-placard">
      <style>{TP_CSS}</style>
      <RainCanvas />
      <div key={openKey} className={`relative z-10 m-3 sm:m-5 border border-yellow-500/40 bg-black/85 backdrop-blur-sm p-5 ${closing ? "tp-closing" : "tp-placard"}`}>
        <E d={0.9} className="flex items-center justify-between gap-2 mb-2">
          <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-yellow-400 font-bold">
            <Zap className="w-3.5 h-3.5" /> Active Tournament — Live Feed
          </span>
          <span className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-green-400">
            <span className="tp-live-dot w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> Live
          </span>
        </E>
        <div data-testid="active-tournament-info">
          <E d={1.05} className="font-display font-black text-2xl uppercase leading-tight text-white">{active.name}</E>
          <E d={1.2} className="text-xs text-zinc-400 mt-0.5">{active.date} · {active.location}</E>
          <E d={1.35} className="text-xs text-zinc-400 mt-0.5">Your team: <span className="text-white font-semibold">{active.team}</span></E>
          <E d={1.5} className="flex flex-wrap gap-1.5 pt-1.5">
            {active.events?.filter(Boolean).map((e) => <Pill key={e} color="#22D3EE">{e}</Pill>)}
          </E>
        </div>
        <E d={1.7} className="border-t border-green-500/20 my-4" />
        <E d={1.85} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-pb-red font-bold mb-2">
          <ClipboardCheck className="w-3.5 h-3.5" /> Game Results
        </E>
        {game ? (
          <>
            <E d={2.0} className="text-sm">
              <span className="text-zinc-400">{game.round_label}{game.event_type ? ` · ${game.event_type}` : ""} — vs </span>
              <span className="font-semibold text-white">{game.opponent}</span>
            </E>
            {game.consensus?.reports > 0 && (
              <E d={2.1} className="mt-1.5">
                <div className="text-[10px] uppercase tracking-widest border border-pb-cyan/40 bg-pb-cyan/5 text-pb-cyan px-2.5 py-1.5 inline-block" data-testid="consensus-badge">
                  {game.consensus.reports} of {game.consensus.needed} confirmations in
                  {game.consensus.waiting_on?.length > 0 && <span className="text-zinc-400"> · waiting on {game.consensus.waiting_on.join(", ")}</span>}
                </div>
              </E>
            )}
            <E d={2.2}>
              <div className="flex items-center gap-3 mt-2">
                <div className="flex-1"><label className={lbl}>Your Team</label>
                  <input data-testid="score-for-input" type="number" min={0} value={scoreFor} onChange={(e) => setScoreFor(e.target.value)} className={input} /></div>
                <span className="text-zinc-600 mt-5">—</span>
                <div className="flex-1"><label className={lbl}>Opponent</label>
                  <input data-testid="score-against-input" type="number" min={0} value={scoreAgainst} onChange={(e) => setScoreAgainst(e.target.value)} className={input} /></div>
              </div>
              <button data-testid="score-submit-btn" onClick={submitScore} disabled={busy || scoreFor === "" || scoreAgainst === ""}
                className="tn-btn tn-reg w-full h-[48px] mt-3 active:scale-95 transition-transform disabled:opacity-40">
                <span className="tn-inner text-[10px] tracking-[0.2em]">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Score"}</span>
              </button>
            </E>
          </>
        ) : (
          <E d={2.0} className="text-sm text-zinc-500" >No active match to report — your next matchup etches in here the moment it's called.</E>
        )}
        {announcements?.length > 0 && (
          <E d={2.4} className="mt-4 space-y-1.5">
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-600">Approved Results</div>
            {announcements.map((a, i) => (
              <div key={i} className={`text-xs px-2.5 py-1.5 border ${a.won ? "border-pb-red/40 bg-pb-red/5 text-pb-red" : "border-pb-border text-zinc-400"}`}>{a.text}</div>
            ))}
          </E>
        )}
        {phase === "confirm" && (
          <div className="absolute inset-0 z-20 bg-black/90 flex flex-col items-center justify-center gap-2 text-center p-4" data-testid="score-confirm-overlay">
            <CheckCircle2 className="w-10 h-10 text-green-400" style={{ filter: "drop-shadow(0 0 14px rgba(74,222,128,.9))" }} />
            <div className="font-display font-black text-xl uppercase text-green-400" style={{ textShadow: "0 0 12px rgba(74,222,128,.7)" }}>
              Score Submitted
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Transmission sent to the Tournament Director · closing placard…</div>
          </div>
        )}
      </div>
    </div>
  );
}
